import { useEffect, useMemo, useState } from "react";
import { ScrollView, Text } from "react-native";
import { useTranslation } from "react-i18next";
import type {
  AthleteTargets,
  GlucoseUnit,
  Plan,
  PlanStarter,
  Program,
} from "@jackedlogs/shared";
import {
  computeWeeklyTargets,
  pickTodaySession,
  programWeeklyPrescription,
} from "@jackedlogs/shared";
import {
  HomeView,
  ProgramComposer,
  ProgramHighlightCarousel,
  QuickGlucoseCard,
  QuickWeightCard,
  createSessionResultsCache,
  resolveTheme,
  useAthleteTargets,
  useCatalogItems,
  usePrograms,
  useSessionResults,
  useLocale,
} from "@jackedlogs/ui";
import { apiFetch, ApiError } from "./api";
import { durableWrite } from "./durableWrite";
import { ExercisePickerMount } from "./ExercisePickerMount";
import { PlanDateField } from "./PlanDateField";
import { lsCache } from "./lsCache";
import { useGlucoseWeb } from "./useGlucoseWeb";
import type { ComposerRequest } from "./useCreateHubWeb";
import { useHomeSignals } from "./useHomeSignals";
import { useProfileDoc } from "./useProfileDoc";
import { useThemePalette } from "./theme";
import { useWorkoutStarter } from "./useWorkoutStarter";

// Stable cache adapters (created once) so web has the same cache-first
// programs/progress backstop mobile gets from AsyncStorage.
const programsLsCache = lsCache<import("@jackedlogs/shared").Program[]>(
  "jl.programs.v1",
);
const progressLsCache = lsCache<
  import("@jackedlogs/ui").ProgressByProgram
>("jl.programsProgress.v1");
// ME-3e — cache-first localStorage adapter for the athlete's own weekly
// targets, mirroring mobile's AsyncStorage-backed cache (useAthleteTargets.ts)
// so a cold web load doesn't blank the composer's prescription mode while the
// network round-trips.
const athleteTargetsLsCache = lsCache<AthleteTargets>("jl.athleteTargets.v1");

// One single-flight cache for the composer's weekly-targets reference feed
// (session history + full muscle-contrib catalog). Module-level so a remount
// of PlanScreen reuses the same in-flight fetch.
const editorResultsCache = createSessionResultsCache();

// THE Home surface on web-rnw (HR-S4) — ONE composed scroll (the shared
// HomeView), the SAME tree mobile's Home tab mounts: hero/carousel →
// readiness → weekly stats → recent PRs → quick entries → badges
// placeholder. Program/plan browsing + management moved to the Create-hub
// (sidebar "Crea"); its intents land here as a composerRequest and open the
// shared scheduler-composer full-screen. Start/New workout open the lifted
// Logger (RW-18) via onStartWorkout — see useWorkoutStarter.ts.
export function PlanScreen({
  onOpenProgress,
  composerRequest,
  onComposerRequestHandled,
  onStartWorkout,
}: {
  // Host nav seam: the readiness badge + recent-PRs taps jump to the
  // Progress tab (now that it's lifted). AppShell owns the tab state.
  onOpenProgress?: () => void;
  // Create-hub seam (HR-S4w): the hub's actions/library land here as a
  // composer request; this surface opens the right composer and reports
  // back so the shell clears the request.
  composerRequest?: ComposerRequest | null;
  onComposerRequestHandled?: () => void;
  // Cross-tab seam (mirrors onOpenProgress/composerRequest): AppShell turns
  // a built PlanStarter (or, for a plain "New workout", no starter at all)
  // into a composerRequest on the Workouts tab + a tab switch. Optional only
  // for prop-shape symmetry with the rest of this file's host seams —
  // AppShell always supplies it.
  onStartWorkout?: (starter?: PlanStarter) => void;
}) {
  const { dark, family } = useThemePalette();
  const { i18n } = useTranslation();
  const lang = useLocale();
  const theme = resolveTheme(dark, family);
  const catalog = useCatalogItems({ apiFetch });
  const programsState = usePrograms({
    apiFetch,
    durableWrite,
    withProgress: true,
    programsCache: programsLsCache,
    progressCache: progressLsCache,
  });
  const { refreshPrograms, setProgramStatus } = programsState;
  const { formBadge, weekly, prs, units, bodyweightKg, refreshProfile } =
    useHomeSignals();
  const { doc } = useProfileDoc();
  const glucoseUnit = (doc?.["glucoseUnit"] as GlucoseUnit | undefined) ?? "mmol";
  const { readings: glucoseReadings, create: createGlucose } = useGlucoseWeb();
  // The FULL exercise catalog (muscleContrib) + raw session results — the
  // SAME shared hook + shape the mobile ProgramEditorScreen injects for the
  // composer's read-only prescription projection. Cache-first; degrades to
  // nothing while null. RW-17 — the AP-3 weekly-targets meter (below) reuses
  // this SAME single-flight fetch instead of a second one, matching how the
  // Progress mount (progress/progressDeps.ts) shares its resultsCache.
  const { results: sessionResults, catalog: fullCatalog } = useSessionResults({
    apiFetch,
    cache: editorResultsCache,
  });
  // ME-3e — the athlete's own weekly targets, so ProgramComposer's
  // prescription mode renders on web instead of silently omitting it (there
  // was no fetch for it here at all). ⚠️ Mobile reads the SAME document
  // through its own hook, not this one — see the note in @jackedlogs/ui's
  // barrel: the repoint is tracked, the sharing is not a fact yet.
  const { value: athleteTargets } = useAthleteTargets({
    apiFetch,
    cache: athleteTargetsLsCache,
  });

  const [editing, setEditing] = useState<Program | null>(null);
  const [creating, setCreating] = useState(false);
  // Plan composer (single mode) — separate full-screen surface, same as the
  // program composer above. editingPlan = edit existing, creatingPlan = new.
  const [editingPlan, setEditingPlan] = useState<Plan | null>(null);
  const [creatingPlan, setCreatingPlan] = useState(false);
  const [mutationError, setMutationError] = useState<string | null>(null);

  // Consume the Create-hub's composer request (create = no item, edit =
  // item attached), then hand the token back so a later remount of this
  // surface doesn't re-open a composer the user already closed.
  useEffect(() => {
    if (!composerRequest) return;
    if (composerRequest.kind === "program") {
      if (composerRequest.program) setEditing(composerRequest.program);
      else setCreating(true);
    } else if (composerRequest.kind === "plan") {
      if (composerRequest.plan) setEditingPlan(composerRequest.plan);
      else setCreatingPlan(true);
    }
    // "workout" requests are consumed by WorkoutsScreen's own
    // composerRequest handling (RW-18) — nothing for the Plan surface to do.
    onComposerRequestHandled?.();
  }, [composerRequest, onComposerRequestHandled]);

  const resolveName = useMemo(() => {
    const bySlug = new Map(catalog.items.map((c) => [c.slug, c.name]));
    return (slug: string, snapshot?: string) =>
      bySlug.get(slug) ?? snapshot ?? slug;
  }, [catalog.items]);

  const today = useMemo(
    () =>
      pickTodaySession(
        programsState.followedPrograms,
        programsState.progress,
        lang,
      ),
    [programsState.followedPrograms, programsState.progress, lang],
  );

  // "Start" (today's session / a program-highlight card) → build a
  // PlanStarter and hand it to AppShell, which opens the Logger on the
  // Workouts tab pre-populated (mirrors mobile's identical useWorkoutStarter
  // wiring in apps/mobile/src/screens/PlanScreen.tsx).
  const { starting, startProgramSession } = useWorkoutStarter(
    catalog.raw ? [...catalog.raw] : null,
    bodyweightKg,
    (starter) => onStartWorkout?.(starter),
  );
  const heroLoading =
    programsState.programsStatus === "loading" &&
    programsState.programs.length === 0;
  const hasFollowed = programsState.followedPrograms.length > 0;

  // RW-17 — the AP-3 three-zone weekly meter. Mirrors the mobile PlanScreen
  // wiring (and the sibling ProgressScreen.tsx pinnedProgram derivation): the
  // pinned "active block" (Profile.currentProgramId) among the followed
  // programs supplies the weekly targets + the prescription 2nd reference;
  // the raw session results (shared with the composer above) supply the
  // actual hard-sets. ME-3e wired a web fetch for athleteTargets (above, for
  // the ProgramComposer mounts below) but this meter is left as-is on purpose
  // — ProgressScreen.tsx's deps.athleteTargets has the SAME omission and
  // fixing one without the other would just make the two Home-adjacent
  // surfaces disagree; that pairing is a separate item. Omitting it here
  // renders the card in "degraded" mode (neutral bars, no scored zones)
  // instead of hiding, per computeWeeklyTargets' own null-athleteTargets
  // fallback.
  const currentProgramId = doc?.["currentProgramId"] as string | undefined;
  const weekStartDay = doc?.["weekStartDay"] as number | undefined;
  const pinnedProgram = useMemo<Program | null>(
    () =>
      currentProgramId
        ? (programsState.followedPrograms.find(
            (p) => p.id === currentProgramId,
          ) ?? null)
        : null,
    [programsState.followedPrograms, currentProgramId],
  );
  const prescription = useMemo(
    () =>
      pinnedProgram && fullCatalog
        ? programWeeklyPrescription(pinnedProgram, fullCatalog)
        : null,
    [pinnedProgram, fullCatalog],
  );
  const weeklyTargets = useMemo(
    () =>
      sessionResults === null || fullCatalog === null
        ? null
        : computeWeeklyTargets(
            sessionResults,
            fullCatalog,
            pinnedProgram,
            Date.now(),
            prescription,
            { weekStartDay },
            // athleteTargets stays at its default; `lang` is the reader's
            // language for the pinned program's name (PE-21).
            null,
            lang,
          ),
    [
      sessionResults,
      fullCatalog,
      pinnedProgram,
      prescription,
      weekStartDay,
      lang,
    ],
  );

  const surfaceError = (err: Error | null) =>
    setMutationError(
      err === null
        ? null
        : err instanceof ApiError
          ? `${err.status}: ${err.message}`
          : err.message,
    );

  const saveWeight = async (canonicalKg: number): Promise<boolean> => {
    try {
      await apiFetch("/api/me/measurements", {
        method: "POST",
        body: { takenAt: new Date().toISOString(), weightKg: canonicalKg },
      });
      await refreshProfile();
      return true;
    } catch {
      return false;
    }
  };

  if (editing || creating) {
    return (
      <ProgramComposer
        key={editing?.id ?? "new"}
        apiFetch={apiFetch}
        durableWrite={durableWrite}
        dark={dark}
        program={editing ?? undefined}
        catalog={catalog.items}
        Picker={ExercisePickerMount}
        exerciseCatalog={fullCatalog ?? undefined}
        unitsPreference={units}
        // ME-3e — mirrors mobile's ProgramEditorScreen: the athlete's own
        // weekly targets, so the composer's read-only prescription
        // projection renders instead of silently omitting it.
        athleteTargets={athleteTargets}
        onSaved={() => {
          setEditing(null);
          setCreating(false);
          void refreshPrograms();
        }}
        onCancel={() => {
          setEditing(null);
          setCreating(false);
        }}
      />
    );
  }

  if (editingPlan || creatingPlan) {
    return (
      <ProgramComposer
        key={editingPlan?.id ?? "new-plan"}
        mode="single"
        apiFetch={apiFetch}
        durableWrite={durableWrite}
        dark={dark}
        plan={editingPlan ?? undefined}
        catalog={catalog.items}
        Picker={ExercisePickerMount}
        DateField={PlanDateField}
        unitsPreference={units}
        onSaved={() => {
          // Nothing to refresh here — the plans list lives in the hub's
          // Library now, which refetches on open.
          setEditingPlan(null);
          setCreatingPlan(false);
        }}
        onCancel={() => {
          setEditingPlan(null);
          setCreatingPlan(false);
        }}
      />
    );
  }

  return (
    <ScrollView
      style={{ flex: 1, backgroundColor: theme.palette.bg }}
      showsVerticalScrollIndicator={false}
    >
      {mutationError ? (
        <Text
          style={{
            color: theme.palette.danger,
            fontSize: theme.typography.sizes.sm,
            marginHorizontal: 12,
            marginTop: 8,
          }}
        >
          {mutationError}
        </Text>
      ) : null}
      <HomeView
        theme={theme}
        today={today}
        resolveName={resolveName}
        heroLoading={heroLoading}
        isStartingToday={today !== null && starting === today.program.id}
        onStartToday={() => {
          if (today !== null) {
            void startProgramSession(today.program, today.weekIdx, today.sessionIdx);
          }
        }}
        onNewWorkout={() => onStartWorkout?.()}
        formBadge={formBadge}
        weekly={weekly}
        // RW-17 — wired to the same computeWeeklyTargets scorer mobile's Home
        // uses, off the composer's shared session-results fetch (above).
        weeklyTargets={weeklyTargets}
        onOpenProgress={onOpenProgress ?? (() => {})}
        prs={prs}
        units={units}
        locale={i18n.language}
        programHighlightSlot={
          hasFollowed ? (
            <ProgramHighlightCarousel
              followedPrograms={programsState.followedPrograms}
              progress={programsState.progress}
              catalog={catalog.raw ? [...catalog.raw] : null}
              resolveName={resolveName}
              theme={theme}
              dark={dark}
              onStart={(program, weekIdx, sessionIdx) =>
                void startProgramSession(program, weekIdx, sessionIdx)
              }
              startingId={starting}
              onUnfollow={(prog) => {
                void setProgramStatus(prog.id, "archived").then(surfaceError);
              }}
              onEdit={setEditing}
            />
          ) : undefined
        }
        quickWeightSlot={
          <QuickWeightCard
            theme={theme}
            units={units}
            bodyweightKg={bodyweightKg}
            queued={0}
            onSave={saveWeight}
          />
        }
        quickGlucoseSlot={
          <QuickGlucoseCard
            theme={theme}
            unit={glucoseUnit}
            readings={glucoseReadings}
            nowMs={Date.now()}
            queued={0}
            onSave={createGlucose}
          />
        }
      />
    </ScrollView>
  );
}
